import { useState } from 'react'
import {
  SimpleGrid, Title, Text, Group, Paper, Stack, Skeleton, Badge,
  ThemeIcon, Table, Card, Alert, ActionIcon, Tooltip, Button, Divider,
  ScrollArea,
} from '@mantine/core'
import {
  IconCalendarEvent, IconCalendarDue, IconLeaf, IconUsers,
  IconAlertCircle, IconCurrencyTugrik, IconRefresh, IconCalendarStats,
  IconUserCheck, IconUsersGroup, IconClockHour4, IconSpeakerphone,
} from '@tabler/icons-react'
import { useAuth } from '@/context/AuthContext'
import { BOOKING_STATUS_CONFIG, SERVICE_CATEGORIES } from '@/constants'
import { useDashboard, useBookings, useServices, useHomepageContents } from '@/lib/swr'
import type { DashboardData, Booking, Customer, Service, HomepageContent } from '@/types'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

// ─── Announcement type color map ─────────────────────────────────────────────

const ANNOUNCEMENT_COLORS: Record<string, string> = {
  info: 'blue',
  warning: 'yellow',
  success: 'green',
  error: 'red',
}

const ANNOUNCEMENT_LABELS: Record<string, string> = {
  info: 'Мэдээлэл',
  warning: 'Анхааруулга',
  success: 'Амжилт',
  error: 'Яаралтай',
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuth()

  const todayStr = dayjs().format('YYYY-MM-DD')

  // Core dashboard stats
  const { data: dashRaw, isLoading: loading, error: dashError, isValidating: refreshing, mutate: refreshDash } = useDashboard()
  const data = (dashRaw as any)?.data as DashboardData | undefined ?? dashRaw as DashboardData | undefined
  const error = dashError ? (dashError instanceof Error ? dashError.message : 'Хянах самбарын мэдээлэл ачаалахад алдаа гарлаа') : null
  const [lastUpdated] = useState<Date | null>(() => new Date())

  // Active announcements
  const { data: announcementsRaw, isLoading: announcementsLoading } = useHomepageContents({ type: 'announcement', status: 'active' })
  const announcements: HomepageContent[] = (announcementsRaw as any)?.data ?? []

  // Today's bookings
  const { data: todayBookingsRaw, isLoading: todayBookingsLoading } = useBookings({ date: todayStr, limit: '50', page: '1' })
  const todayBookings: Booking[] = ((todayBookingsRaw as any)?.data ?? []).slice().sort((a: Booking, b: Booking) => {
    const timeA = a.timeSlot?.startTime ?? ''
    const timeB = b.timeSlot?.startTime ?? ''
    return timeA.localeCompare(timeB)
  })

  // Active services
  const { data: servicesRaw, isLoading: activeServicesLoading } = useServices({ status: 'active', limit: '6', page: '1' })
  const activeServices: Service[] = (servicesRaw as any)?.data ?? []

  const fetchDashboard = () => { refreshDash() }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const greeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Өглөөний мэнд'
    if (hour < 18) return 'Өдрийн мэнд'
    return 'Оройн мэнд'
  }

  const displayName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
    : ''

  const pendingCount = data?.bookings?.byStatus?.['pending'] ?? 0

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Stack gap="lg">
      {/* ── Greeting + Refresh ── */}
      <Group justify="space-between" align="flex-start">
        <div>
          {loading ? (
            <>
              <Skeleton h={32} w={300} mb={6} />
              <Skeleton h={18} w={180} />
            </>
          ) : (
            <>
              <Title order={2} fw={700}>
                {greeting()}{displayName ? `, ${displayName}` : ''}
              </Title>
              <Group gap={6} mt={2}>
                <Text size="sm" c="dimmed">
                  NatureMed — Өнөөдрийн тойм
                </Text>
                {lastUpdated && (
                  <Text size="xs" c="dimmed">
                    · {dayjs(lastUpdated).format('HH:mm')}-д шинэчлэгдсэн
                  </Text>
                )}
              </Group>
            </>
          )}
        </div>
        <Tooltip label="Шинэчлэх" withArrow>
          <ActionIcon
            variant="light"
            color="gray"
            size="lg"
            radius="md"
            loading={refreshing}
            disabled={loading}
            onClick={() => fetchDashboard()}
          >
            <IconRefresh size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* ── Error state ── */}
      {error && (
        <Alert
          icon={<IconAlertCircle size={18} />}
          title="Алдаа"
          color="red"
          variant="light"
        >
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<IconRefresh size={14} />}
              onClick={() => fetchDashboard()}
            >
              Дахин оролдох
            </Button>
          </Group>
        </Alert>
      )}

      {/* ── Pending bookings alert ── */}
      {!loading && !error && pendingCount > 0 && (
        <Alert
          icon={<IconClockHour4 size={18} />}
          title="Анхаарал"
          color="yellow"
          variant="light"
        >
          <Text size="sm">
            <strong>{pendingCount}</strong> хүлээгдэж буй захиалга баталгаажуулалт хүлээж байна.
          </Text>
        </Alert>
      )}

      {/* ── Active Announcements (dynamic from homepage-content API) ── */}
      {announcementsLoading ? (
        <Stack gap="xs">
          <Skeleton h={56} radius="md" />
        </Stack>
      ) : announcements.length > 0 ? (
        <Stack gap="xs">
          {announcements.map((ann) => {
            const color = ANNOUNCEMENT_COLORS[ann.announcementType ?? 'info'] ?? 'blue'
            const typeLabel = ANNOUNCEMENT_LABELS[ann.announcementType ?? 'info'] ?? 'Мэдээлэл'
            return (
              <Alert
                key={ann._id}
                icon={<IconSpeakerphone size={16} />}
                color={color}
                variant="light"
                title={
                  <Group gap={8}>
                    <Text size="sm" fw={600}>{ann.title}</Text>
                    <Badge size="xs" color={color} variant="filled">{typeLabel}</Badge>
                  </Group>
                }
              >
                {ann.content ? (
                  <Text size="sm">{ann.content}</Text>
                ) : null}
                {(ann.startDate || ann.endDate) && (
                  <Text size="xs" c="dimmed" mt={4}>
                    {ann.startDate ? dayjs(ann.startDate).format('YYYY.MM.DD') : ''}
                    {ann.startDate && ann.endDate ? ' – ' : ''}
                    {ann.endDate ? dayjs(ann.endDate).format('YYYY.MM.DD') : ''}
                  </Text>
                )}
              </Alert>
            )
          })}
        </Stack>
      ) : null}

      {/* ── Stats Row 1: Bookings & Services ── */}
      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} h={100} radius="md" />
          ))
        ) : data ? (
          <>
            <Paper withBorder p="md" radius="md">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Нийт захиалга
                  </Text>
                  <Text fw={700} size="xl" mt={4}>
                    {data.bookings?.total ?? 0}
                  </Text>
                  <Text size="xs" c="dimmed">Бүх цагийн</Text>
                </div>
                <ThemeIcon color="blue" variant="light" size={44} radius="md">
                  <IconCalendarEvent size={22} stroke={1.5} />
                </ThemeIcon>
              </Group>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Өнөөдрийн захиалга
                  </Text>
                  <Text fw={700} size="xl" mt={4}>
                    {data.bookings?.today ?? 0}
                  </Text>
                  <Text size="xs" c="dimmed">{dayjs().format('YYYY.MM.DD')}</Text>
                </div>
                <ThemeIcon color="teal" variant="light" size={44} radius="md">
                  <IconCalendarDue size={22} stroke={1.5} />
                </ThemeIcon>
              </Group>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Идэвхтэй үйлчилгээ
                  </Text>
                  <Text fw={700} size="xl" mt={4}>
                    {data.services?.active ?? 0}
                  </Text>
                  <Text size="xs" c="dimmed">Нийт: {data.services?.total ?? 0}</Text>
                </div>
                <ThemeIcon color="green" variant="light" size={44} radius="md">
                  <IconLeaf size={22} stroke={1.5} />
                </ThemeIcon>
              </Group>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Нийт үйлчлүүлэгч
                  </Text>
                  <Text fw={700} size="xl" mt={4}>
                    {data.customers?.total ?? 0}
                  </Text>
                  <Text size="xs" c="dimmed">Идэвхтэй: {data.customers?.active ?? 0}</Text>
                </div>
                <ThemeIcon color="violet" variant="light" size={44} radius="md">
                  <IconUsers size={22} stroke={1.5} />
                </ThemeIcon>
              </Group>
            </Paper>
          </>
        ) : null}
      </SimpleGrid>

      {/* ── Stats Row 2: Monthly, Weekly, Active customers, Users ── */}
      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} h={100} radius="md" />
          ))
        ) : data ? (
          <>
            <Paper withBorder p="md" radius="md">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Энэ сарын захиалга
                  </Text>
                  <Text fw={700} size="xl" mt={4}>
                    {data.bookings?.monthly ?? 0}
                  </Text>
                  <Text size="xs" c="dimmed">{dayjs().format('YYYY-MM')} сар</Text>
                </div>
                <ThemeIcon color="orange" variant="light" size={44} radius="md">
                  <IconCalendarStats size={22} stroke={1.5} />
                </ThemeIcon>
              </Group>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Энэ 7 хоногт
                  </Text>
                  <Text fw={700} size="xl" mt={4}>
                    {data.bookings?.weekly ?? 0}
                  </Text>
                  <Text size="xs" c="dimmed">Сүүлийн 7 хоног</Text>
                </div>
                <ThemeIcon color="cyan" variant="light" size={44} radius="md">
                  <IconCalendarDue size={22} stroke={1.5} />
                </ThemeIcon>
              </Group>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Идэвхтэй үйлчлүүлэгч
                  </Text>
                  <Text fw={700} size="xl" mt={4}>
                    {data.customers?.active ?? 0}
                  </Text>
                  <Text size="xs" c="dimmed">Нийт: {data.customers?.total ?? 0}</Text>
                </div>
                <ThemeIcon color="grape" variant="light" size={44} radius="md">
                  <IconUserCheck size={22} stroke={1.5} />
                </ThemeIcon>
              </Group>
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Системийн хэрэглэгч
                  </Text>
                  <Text fw={700} size="xl" mt={4}>
                    {data.users?.total ?? 0}
                  </Text>
                  <Text size="xs" c="dimmed">Бүртгэлтэй</Text>
                </div>
                <ThemeIcon color="indigo" variant="light" size={44} radius="md">
                  <IconUsersGroup size={22} stroke={1.5} />
                </ThemeIcon>
              </Group>
            </Paper>
          </>
        ) : null}
      </SimpleGrid>

      {/* ── Middle Row: Booking Status + Revenue ── */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {loading ? (
          <>
            <Skeleton h={220} radius="md" />
            <Skeleton h={220} radius="md" />
          </>
        ) : data ? (
          <>
            {/* Booking Status Distribution */}
            <Paper withBorder p="lg" radius="md">
              <Text fw={600} size="sm" mb="md">
                Захиалгын статус хуваарилалт
              </Text>
              <Stack gap={10}>
                {Object.entries(BOOKING_STATUS_CONFIG).map(([key, cfg]) => {
                  const count = data.bookings?.byStatus?.[key] ?? 0
                  const total = data.bookings?.total || 1
                  const pct = Math.round((count / total) * 100)
                  return (
                    <div key={key}>
                      <Group justify="space-between" mb={4}>
                        <Badge variant="dot" color={cfg.color} size="sm">
                          {cfg.label}
                        </Badge>
                        <Group gap={6}>
                          <Text size="sm" fw={600}>{count}</Text>
                          <Text size="xs" c="dimmed">({pct}%)</Text>
                        </Group>
                      </Group>
                    </div>
                  )
                })}
                <Divider />
                <Group justify="space-between">
                  <Text size="xs" c="dimmed" fw={600}>Нийт</Text>
                  <Text size="sm" fw={700}>{data.bookings?.total ?? 0}</Text>
                </Group>
              </Stack>
            </Paper>

            {/* Revenue */}
            <Paper withBorder p="lg" radius="md">
              <Text fw={600} size="sm" mb="md">
                Орлого ба захиалга
              </Text>
              <Stack gap="md">
                <Paper withBorder p="md" radius="md">
                  <Group justify="space-between">
                    <div>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Энэ сарын орлого
                      </Text>
                      <Group gap={4} mt={4}>
                        <IconCurrencyTugrik size={18} color="var(--mantine-color-green-6)" />
                        <Text fw={700} size="lg">
                          {(data.monthlyRevenue || 0).toLocaleString()}
                        </Text>
                      </Group>
                    </div>
                    <ThemeIcon color="green" variant="light" size={40} radius="md">
                      <IconCurrencyTugrik size={20} />
                    </ThemeIcon>
                  </Group>
                </Paper>
                <Paper withBorder p="md" radius="md">
                  <Group justify="space-between">
                    <div>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Сарын захиалга
                      </Text>
                      <Group gap={6} mt={4} align="baseline">
                        <Text fw={700} size="lg">
                          {data.bookings?.monthly ?? 0}
                        </Text>
                        <Text size="xs" c="dimmed">захиалга</Text>
                      </Group>
                    </div>
                    <ThemeIcon color="blue" variant="light" size={40} radius="md">
                      <IconCalendarStats size={20} />
                    </ThemeIcon>
                  </Group>
                </Paper>
                <Paper withBorder p="md" radius="md">
                  <Group justify="space-between">
                    <div>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        7 хоногийн захиалга
                      </Text>
                      <Group gap={6} mt={4} align="baseline">
                        <Text fw={700} size="lg" c="dimmed">
                          {data.bookings?.weekly ?? 0}
                        </Text>
                        <Text size="xs" c="dimmed">захиалга</Text>
                      </Group>
                    </div>
                    <ThemeIcon color="cyan" variant="light" size={40} radius="md">
                      <IconCalendarEvent size={20} />
                    </ThemeIcon>
                  </Group>
                </Paper>
              </Stack>
            </Paper>
          </>
        ) : null}
      </SimpleGrid>

      {/* ── Bottom Row: Today's Schedule + Active Services ── */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">

        {/* Today's Schedule (dynamic) */}
        {todayBookingsLoading ? (
          <Skeleton h={280} radius="md" />
        ) : (
          <Card shadow="xs" padding={0} radius="md" withBorder>
            <Group justify="space-between" p="md" pb={0}>
              <Group gap={8}>
                <ThemeIcon color="teal" variant="light" size={28} radius="md">
                  <IconCalendarDue size={16} />
                </ThemeIcon>
                <Text fw={600} size="sm">
                  Өнөөдрийн хуваарь
                </Text>
              </Group>
              <Group gap={6}>
                <Badge variant="light" color="teal" size="sm">
                  {dayjs().format('YYYY.MM.DD')}
                </Badge>
                <Badge variant="light" color="gray" size="sm">
                  {todayBookings.length} захиалга
                </Badge>
              </Group>
            </Group>

            {todayBookings.length === 0 ? (
              <Stack align="center" justify="center" py="xl" px="md">
                <IconCalendarDue size={36} color="var(--mantine-color-dimmed)" stroke={1.2} />
                <Text c="dimmed" size="sm" ta="center">
                  Өнөөдөр захиалга байхгүй байна.
                </Text>
              </Stack>
            ) : (
              <ScrollArea h={240} mt="xs">
                <Table verticalSpacing="xs" horizontalSpacing="md" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                      <Table.Th fz="xs" c="dimmed" fw={700} tt="uppercase">Цаг</Table.Th>
                      <Table.Th fz="xs" c="dimmed" fw={700} tt="uppercase">Үйлчлүүлэгч</Table.Th>
                      <Table.Th fz="xs" c="dimmed" fw={700} tt="uppercase">Үйлчилгээ</Table.Th>
                      <Table.Th fz="xs" c="dimmed" fw={700} tt="uppercase">Статус</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {todayBookings.map((booking: Booking) => {
                      const customer = booking.customer as Customer
                      const service = booking.service as Service
                      const statusCfg = BOOKING_STATUS_CONFIG[booking.status]
                      return (
                        <Table.Tr key={booking._id}>
                          <Table.Td>
                            <Text size="xs" fw={600} c="teal">
                              {booking.timeSlot?.startTime}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {booking.timeSlot?.endTime}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {customer?.lastName
                                ? `${customer.lastName.charAt(0)}.`
                                : ''}
                              {customer?.firstName || '-'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" lineClamp={1}>
                              {service?.name || '-'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              variant="light"
                              color={statusCfg?.color || 'gray'}
                              size="xs"
                            >
                              {statusCfg?.label || booking.status}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Card>
        )}

        {/* Active Services (dynamic) */}
        {activeServicesLoading ? (
          <Skeleton h={280} radius="md" />
        ) : (
          <Card shadow="xs" padding="md" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Group gap={8}>
                <ThemeIcon color="green" variant="light" size={28} radius="md">
                  <IconLeaf size={16} />
                </ThemeIcon>
                <Text fw={600} size="sm">
                  Идэвхтэй үйлчилгээнүүд
                </Text>
              </Group>
              <Badge variant="light" color="green" size="sm">
                {activeServices.length} үйлчилгээ
              </Badge>
            </Group>

            {activeServices.length === 0 ? (
              <Stack align="center" justify="center" py="xl">
                <IconLeaf size={36} color="var(--mantine-color-dimmed)" stroke={1.2} />
                <Text c="dimmed" size="sm" ta="center">
                  Идэвхтэй үйлчилгээ байхгүй байна.
                </Text>
              </Stack>
            ) : (
              <Stack gap={8}>
                {activeServices.map((service: Service) => (
                  <Paper key={service._id} withBorder p="sm" radius="md">
                    <Group justify="space-between" wrap="nowrap">
                      <div style={{ minWidth: 0 }}>
                        <Text size="sm" fw={600} lineClamp={1}>
                          {service.name}
                        </Text>
                        <Group gap={6} mt={2}>
                          <Badge size="xs" variant="outline" color="gray">
                            {SERVICE_CATEGORIES[service.category] ?? service.category}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            {service.duration} мин
                          </Text>
                        </Group>
                      </div>
                      <Group gap={4} wrap="nowrap">
                        <IconCurrencyTugrik size={14} color="var(--mantine-color-green-6)" />
                        <Text size="sm" fw={700} c="green">
                          {service.price.toLocaleString()}
                        </Text>
                      </Group>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            )}
          </Card>
        )}
      </SimpleGrid>

      {/* ── Recent Bookings ── */}
      {loading ? (
        <Skeleton h={300} radius="md" />
      ) : data?.recentBookings && data.recentBookings.length > 0 ? (
        <Card shadow="xs" padding={0} radius="md" withBorder>
          <Group justify="space-between" p="md" pb={0}>
            <Group gap={8}>
              <ThemeIcon color="blue" variant="light" size={28} radius="md">
                <IconCalendarEvent size={16} />
              </ThemeIcon>
              <Text fw={600} size="sm">Сүүлийн захиалгууд</Text>
            </Group>
            <Badge variant="light" color="gray" size="sm">
              {data.recentBookings.length}
            </Badge>
          </Group>
          <Table.ScrollContainer minWidth={700}>
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлчлүүлэгч</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлчилгээ</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Огноо</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Цаг</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Статус</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.recentBookings.map((booking: Booking) => {
                  const customer = booking.customer as Customer
                  const service = booking.service as Service
                  const statusCfg = BOOKING_STATUS_CONFIG[booking.status]
                  return (
                    <Table.Tr key={booking._id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {customer?.lastName
                            ? `${customer.lastName.charAt(0)}.`
                            : ''}
                          {customer?.firstName || '-'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{service?.name || '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {dayjs(booking.date).format('YYYY.MM.DD')}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {booking.timeSlot?.startTime} – {booking.timeSlot?.endTime}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          variant="light"
                          color={statusCfg?.color || 'gray'}
                          size="sm"
                        >
                          {statusCfg?.label || booking.status}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      ) : !loading && !error ? (
        <Paper withBorder p="xl" radius="md" ta="center">
          <Text c="dimmed" size="sm">Одоогоор захиалга байхгүй байна.</Text>
        </Paper>
      ) : null}
    </Stack>
  )
}
